/**
 * Wraps a promise so it rejects immediately when the AbortSignal fires,
 * even if the underlying work (e.g. an API call) is still in-flight.
 *
 * This prevents the client from waiting on stale results after navigation
 * and stops sequential chains of calls from continuing.
 *
 * Ported from corp-dashboard/lib/cancellable.ts (same behaviour).
 */
export function cancellable<T>(signal: AbortSignal, fn: () => Promise<T>): Promise<T> {
  if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));

  return Promise.race([
    fn(),
    new Promise<never>((_, reject) => {
      signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
    }),
  ]);
}

export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('abort') || msg.includes('cancelled') || msg.includes('canceled')) return true;
  }
  if (typeof error === 'string' && error.toLowerCase().includes('abort')) return true;
  return false;
}
